/**
 * TrialWelcomeHero — Verkaufsstarker Begrüßungs-Block für Trial-User.
 *
 * Zwei Zustände:
 *   1. ``casesUsed = 0``  → großer „Erstelle deine erste Akte"-Hero
 *      mit Feature-Highlights und CTA → ``onCreateCase``
 *   2. ``casesUsed >= 1`` → kompakter „1/1 Test-Akten verbraucht"-Banner
 *      mit Plan-Wahl-CTA → ``onUpsell``
 */
import React from "react";
import { Button } from "@/components/ui/button";
import { Sparkles, Camera, FileText, Calculator, Mic, ShieldCheck, ArrowRight, Plus, Zap } from "lucide-react";

const HIGHLIGHTS = [
  { icon: Camera,      title: "Mobil & Vor-Ort",       text: "Akte direkt am Schadenort anlegen — Fotos, Stimmnotizen, GPS." },
  { icon: Calculator,  title: "DAT-Integration",       text: "VIN-Lupe lädt Fahrzeugdaten in Sekunden — KBA-Lookup inklusive." },
  { icon: Mic,         title: "KI-Texte & Diktat",     text: "Schadenhergang diktieren → KI formuliert das fertige Gutachten." },
  { icon: FileText,    title: "Profi-PDF mit 1 Klick", text: "Druckfertiges Gutachten + Honorarrechnung + SEPA-Abtretung." },
];

export default function TrialWelcomeHero({ userName, casesUsed = 0, casesMax = 1, onCreateCase, onUpsell, creating }) {
  const firstName = (userName || "").split(" ")[0] || "Gutachter";

  // Mode 1: Noch keine Akte erstellt → großer Verkaufs-Hero
  if (casesUsed === 0) {
    return (
      <div className="mb-8 rounded-3xl overflow-hidden shadow-xl border border-cyan-200/60" data-testid="trial-welcome-hero">
        <div className="brand-gradient-bg text-white px-6 py-10 sm:px-10 sm:py-12 relative overflow-hidden">
          {/* dezente Sphere im Hintergrund */}
          <div className="absolute -right-12 -top-12 w-64 h-64 rounded-full bg-white/10 blur-3xl pointer-events-none" />
          <div className="absolute -left-12 -bottom-12 w-72 h-72 rounded-full bg-cyan-300/20 blur-3xl pointer-events-none" />

          <div className="relative max-w-3xl">
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] font-bold bg-white/15 backdrop-blur-sm rounded-full px-3 py-1.5 mb-4">
              <Sparkles className="h-3.5 w-3.5" />
              <span>Willkommen, {firstName}!</span>
            </div>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.05] tracking-tight">
              Lerne Easy Gutachter mit deiner ersten <span className="text-cyan-100">kostenlosen Test-Akte</span> kennen.
            </h1>
            <p className="mt-3 text-base sm:text-lg text-white/90 max-w-2xl">
              Erlebe in 5 Minuten, wie schnell ein professionelles Gutachten entsteht — mit DAT-Daten, KI-Texten, PDF-Export und SEPA-Abtretung. Keine Kreditkarte nötig.
            </p>

            <div className="mt-7 flex flex-wrap gap-3 items-center">
              <Button
                onClick={onCreateCase}
                disabled={creating}
                size="lg"
                className="bg-white text-cyan-700 hover:bg-cyan-50 font-bold shadow-lg"
                data-testid="trial-hero-create"
              >
                <Plus className="h-5 w-5 mr-2" />
                {creating ? "Erstelle Test-Akte..." : "Test-Akte starten"}
              </Button>
              <button
                onClick={onUpsell}
                className="inline-flex items-center gap-2 text-sm font-semibold text-white/95 hover:text-white underline-offset-4 hover:underline"
                data-testid="trial-hero-plans"
              >
                Pläne ansehen <ArrowRight className="h-4 w-4" />
              </button>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-white/85">
              <span className="flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> SEPA-Lastschrift — keine Kreditkarte</span>
              <span className="flex items-center gap-1.5"><Zap className="h-3.5 w-3.5" /> Sofort einsatzbereit</span>
              <span>3 Monate Mindestlaufzeit bei Abo</span>
            </div>
          </div>
        </div>

        {/* Feature-Highlights unter dem Hero */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-200">
          {HIGHLIGHTS.map(({ icon: Icon, title, text }) => (
            <div key={title} className="bg-white p-5 hover:bg-cyan-50/40 transition" data-testid={`trial-feature-${title}`}>
              <div className="h-9 w-9 rounded-xl bg-cyan-100 text-cyan-700 flex items-center justify-center mb-3">
                <Icon className="h-4 w-4" strokeWidth={1.8} />
              </div>
              <div className="font-display font-bold text-sm">{title}</div>
              <div className="text-xs text-zinc-500 mt-1 leading-snug">{text}</div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Mode 2: User hat seine Test-Akte → kompakter Upsell-Banner
  return (
    <div className="mb-6 rounded-2xl border-2 border-cyan-300 bg-gradient-to-br from-cyan-50 via-white to-cyan-50/50 p-5 sm:p-6" data-testid="trial-upsell-banner">
      <div className="flex flex-wrap items-start gap-5">
        <div className="h-12 w-12 rounded-2xl brand-gradient-bg flex items-center justify-center shrink-0">
          <Sparkles className="h-5 w-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-cyan-700">Test-Modus · {casesUsed}/{casesMax} Akten verbraucht</div>
          <h2 className="font-display text-lg sm:text-xl font-bold mt-1">Bereit für unbegrenzt viele Gutachten?</h2>
          <p className="text-sm text-zinc-600 mt-1 max-w-2xl leading-relaxed">
            Du hast deine kostenlose Test-Akte angelegt. Schließe jetzt ein Abo ab und nutze Easy Gutachter ohne Limits — DAT, KI-Texte, Backoffice, Buchhaltung und vieles mehr.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button onClick={onUpsell} className="brand-gradient-bg text-white" data-testid="trial-upsell-cta">
              Plan wählen <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
            <Button onClick={onCreateCase} variant="outline" disabled data-testid="trial-disabled-create">
              <Plus className="h-4 w-4 mr-1.5" />
              Weitere Akte (Abo nötig)
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
